/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Modal,
  Typography,
  Row,
  Grid,
  Button,
  Col,
  Input,
  Checkbox,
  Spin,
  message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';
import { onError } from 'utils/handlers';
import { getPeriodosDeNominas } from 'api/nominas/periodos/periodosDeNominas';
import { getPeriodosDeNominasDetalles } from 'api/nominas/periodos/periodosDeNominasDetalle';
import { postBonificacionEmpleado } from 'api/nominas/bonificaciones/bonificacionesEmpleados';
import Select from 'components/Select';

const { Text } = Typography;

const namePeriodoDeNomina = 'periodo_de_nomina';

const requiredRule = { required: true, message: 'El campo es requerido' };
const rules = { required: [requiredRule] };

/** El "descuento anterior" que se reembolsa: el único (o primero) Descuento
 * Empleado que la incidencia ya generó. El usuario no captura el importe
 * directamente -- en vez de eso ve el descuento desglosado día por día y
 * puede descartar los días que no quiere bonificar. */
const getDescuentoAnterior = (record) => (record?.descuentos_empleados || [])[0];

const redondear = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

const claveDia = (registroId, indice) => `${registroId}-${indice}`;

const celda = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0' };

/** Celdas de "Día" / "Fecha" / "Importe del Día" / "Bonificar" de una fila,
 * según su caso: sin Descuento Empleado asociado, ya bonificada por completo
 * (agotado), o con un día pendiente que se puede incluir/excluir. */
const CeldasDia = ({
  fila, diasClave, diasExcluidos, onToggleDia,
}) => {
  if (fila.agotado) {
    return (
      <td style={celda} colSpan={4}>
        <Text type="secondary">
          Ya se bonificó la totalidad de los días de este descuento.
        </Text>
      </td>
    );
  }
  if (!fila.descuentoAnterior) {
    return <td style={celda} colSpan={4} aria-label="Sin descuento asociado" />;
  }
  return (
    <>
      <td style={{ ...celda, textAlign: 'center' }}>
        {`Día ${fila.numeroDia} de ${fila.numDiasTotal}`}
      </td>
      <td style={{ ...celda, textAlign: 'center' }}>
        {fila.fecha || 'N/D'}
      </td>
      <td style={{ ...celda, textAlign: 'right' }}>
        {fila.importePorDia}
      </td>
      <td style={{ ...celda, textAlign: 'center' }}>
        <Checkbox
          checked={!diasExcluidos[diasClave]}
          onChange={(e) => onToggleDia(diasClave, e.target.checked)}
        />
      </td>
    </>
  );
};

/** Días del descuento anterior que ya fueron reembolsados por CUALQUIER
 * Bonificación Empleado ya creada (sin importar si esa bonificación en sí ya
 * fue autorizada o sigue en borrador). */
const diasYaBonificados = (descuentoAnterior) => Math.round(
  descuentoAnterior?.dias_bonificados_acumulados || 0,
);

/** Un registro ya no tiene nada pendiente por bonificar cuando la suma de días
 * ya bonificados alcanza o supera el total de días originalmente descontados. */
const yaEstaCompletamenteBonificado = (registro) => {
  const descuentoAnterior = getDescuentoAnterior(registro);
  if (!descuentoAnterior) return false;
  const diasTotales = descuentoAnterior.dias_descontados || registro.cantidad_descuento;
  if (!diasTotales) return false;
  return diasYaBonificados(descuentoAnterior) >= Math.round(diasTotales);
};

/** Aplana los registros en una fila por cada día descontado PENDIENTE de
 * bonificar (para poder desglosarlos y descartarlos uno por uno), conservando
 * rowSpan para no repetir Empleado/Descuento en cada fila. Los días que ya
 * fueron bonificados en una Bonificación anterior no se vuelven a ofrecer. */
const construirFilas = (registros) => {
  const filas = [];
  registros.forEach((registro) => {
    const descuentoAnterior = getDescuentoAnterior(registro);
    if (!descuentoAnterior) {
      filas.push({
        registro,
        descuentoAnterior: null,
        esPrimera: true,
        numDias: 1,
        indice: 0,
        importePorDia: null,
      });
      return;
    }
    const importeTotal = descuentoAnterior.importe_fijo ?? descuentoAnterior.importe_base ?? 0;
    // dias_descontados viene del Descuento Empleado ligado; si por alguna
    // razón no está disponible, se usa cantidad_descuento del propio
    // registro (Guardar Incidencias), que siempre existe para este flujo.
    const diasTotales = descuentoAnterior.dias_descontados || registro.cantidad_descuento;
    const numDiasTotal = diasTotales ? Math.max(1, Math.round(diasTotales)) : 1;
    const diasBonificadosPrevio = Math.min(diasYaBonificados(descuentoAnterior), numDiasTotal);
    const diasRestantes = numDiasTotal - diasBonificadosPrevio;
    if (diasRestantes <= 0) {
      filas.push({
        registro,
        descuentoAnterior,
        esPrimera: true,
        numDias: 1,
        indice: 0,
        importePorDia: null,
        agotado: true,
      });
      return;
    }
    const importePorDia = redondear(importeTotal / numDiasTotal);
    for (let indice = 0; indice < diasRestantes; indice += 1) {
      const numeroDia = diasBonificadosPrevio + indice + 1;
      filas.push({
        registro,
        descuentoAnterior,
        esPrimera: indice === 0,
        numDias: diasRestantes,
        indice,
        numeroDia,
        numDiasTotal,
        importePorDia,
        // Fecha exacta de ese día, si el número de fechas reconstruidas
        // coincide con el total de días del descuento -- ver la salvedad en
        // el backend (obtener_fechas_de_incidencia) sobre conceptos con
        // umbrales, donde este cruce por posición no es confiable.
        fecha: registro.fechas_incidencia?.length === numDiasTotal
          ? registro.fechas_incidencia[numeroDia - 1]
          : null,
      });
    }
  });
  return filas;
};

const IncidenciaBonificar = ({
  open, onCancel, onSuccess, records,
}) => {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const periodoDeNominaWatcher = Form.useWatch(namePeriodoDeNomina, form);

  const [periodosDeNomina, setPeriodosDeNomina] = useState([]);
  const [periodosDeNominaDetalle, setPeriodosDeNominaDetalle] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diasExcluidos, setDiasExcluidos] = useState({});

  const registros = records || [];
  const tiposDeNominaEmpleadoIds = [
    ...new Set(registros.map((r) => r.tipo_de_nomina_empleado_id).filter(Boolean)),
  ];
  const registrosSinDescuento = registros.filter((r) => !getDescuentoAnterior(r));
  const registrosCompletamenteBonificados = registros.filter(
    (r) => getDescuentoAnterior(r) && yaEstaCompletamenteBonificado(r),
  );
  const filas = construirFilas(registros);

  useEffect(() => {
    if (!open || !registros.length) {
      return;
    }
    const loadPeriodos = async () => {
      setLoading(true);
      const _periodosDeNomina = await getPeriodosDeNominas({
        estados_globales: ESTATUS_AUTORIZADO,
      });
      setPeriodosDeNomina(
        (_periodosDeNomina || []).filter(
          (p) => tiposDeNominaEmpleadoIds.includes(p.tipo_de_nomina_empleado),
        ),
      );
      setLoading(false);
    };
    loadPeriodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, records]);

  useEffect(() => {
    if (!periodoDeNominaWatcher) {
      setPeriodosDeNominaDetalle([]);
      return;
    }
    const loadDetalles = async () => {
      const _detalles = await getPeriodosDeNominasDetalles({
        periodo_de_nomina: periodoDeNominaWatcher,
      });
      setPeriodosDeNominaDetalle(_detalles || []);
    };
    loadDetalles();
  }, [periodoDeNominaWatcher]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setDiasExcluidos({});
      return;
    }
    form.setFieldsValue({
      observaciones: '',
      [namePeriodoDeNomina]: undefined,
      periodo_de_nomina_detalle: undefined,
    });
    // Por defecto se bonifican todos los días descontados (ninguno excluido);
    // el usuario descarta los que no quiera bonificar destildándolos.
    setDiasExcluidos({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, records]);

  const onFinish = async () => {
    if (registrosSinDescuento.length) {
      message.warn(
        'No se puede bonificar: los siguientes empleados no tienen un Descuento '
        + `Empleado asociado a esta incidencia: ${registrosSinDescuento
          .map((r) => r.empleado_numero_y_nombre_completo || `#${r.id}`).join(', ')}.`,
      );
      return;
    }
    if (registrosCompletamenteBonificados.length) {
      message.warn(
        'No se puede bonificar: los siguientes empleados ya tienen bonificada la '
        + `totalidad de los días de este descuento: ${registrosCompletamenteBonificados
          .map((r) => r.empleado_numero_y_nombre_completo || `#${r.id}`).join(', ')}.`,
      );
      return;
    }
    try {
      setLoading(true);
      const values = await form.validateFields();

      const registrosAEnviar = registros
        .map((registro) => {
          const descuentoAnterior = getDescuentoAnterior(registro);
          const filasDelRegistro = filas.filter((f) => f.registro.id === registro.id && !f.agotado);
          const diasIncluidos = filasDelRegistro.filter(
            (f) => !diasExcluidos[claveDia(registro.id, f.indice)],
          );
          return { registro, descuentoAnterior, diasIncluidos };
        })
        .filter(({ diasIncluidos }) => diasIncluidos.length > 0);

      if (!registrosAEnviar.length) {
        message.warn('Selecciona al menos un día para bonificar.');
        setLoading(false);
        return;
      }

      const resultados = await Promise.all(registrosAEnviar.map((item) => {
        const { registro, descuentoAnterior, diasIncluidos } = item;
        const importePorDia = diasIncluidos[0]?.importePorDia;
        const diasBonificados = diasIncluidos.length;
        const importe = importePorDia !== null && importePorDia !== undefined
          ? redondear(importePorDia * diasBonificados)
          : (descuentoAnterior?.importe_fijo ?? descuentoAnterior?.importe_base);
        const tieneDias = descuentoAnterior?.dias_descontados || registro.cantidad_descuento;
        return postBonificacionEmpleado({
          importe,
          dias_bonificados: tieneDias ? diasBonificados : null,
          observaciones: values.observaciones || '',
          empleado: registro.empleado_id,
          descuento_empleado: descuentoAnterior?.id ?? null,
          guardar_incidencia: registro.guardar_incidencia_id,
          evaluacion_cierre: registro.evaluacion_cierre_id,
          periodo_de_nomina_detalle: values.periodo_de_nomina_detalle,
          estados_globales: ESTATUS_ACTIVO,
        });
      }));
      if (resultados.every((r) => r)) {
        onSuccess();
      }
    } catch (err) {
      if (err?.errorFields) {
        // Errores de validación del Form, ya se muestran en los campos.
      } else {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Modal
        title={(
          <Row justify="space-between">
            <Text>
              {`Bonificar (${registros.length} ${registros.length === 1 ? 'registro' : 'registros'})`}
            </Text>
            <Row className="form-submit-controls">
              <Button
                type="link"
                onClick={onCancel}
                disabled={loading}
              >
                <ArrowLeftOutlined />
                {screens.md && 'Cancelar'}
              </Button>
              <Button
                type="link"
                onClick={onFinish}
                disabled={loading}
                style={{ marginLeft: 10 }}
                className="submit-delete"
                autoFocus
              >
                <SaveOutlined />
                {screens.md && 'Guardar'}
              </Button>
            </Row>
          </Row>
        )}
        className="modal-delete"
        open={open}
        footer={null}
        closable={false}
        maskClosable={!loading}
        keyboard={!loading}
        destroyOnClose
        width={1150}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
        >
          <Col xs={24} sm={24} md={24}>
            <Text strong style={{ fontSize: 15 }}>Empleados a bonificar</Text>
            <table className="ant-table" style={{ width: '100%', marginTop: 12, marginBottom: 20 }}>
              <thead>
                <tr>
                  <th style={{ ...celda, textAlign: 'left' }}>Empleado</th>
                  <th style={{ ...celda, textAlign: 'left' }}>Descuento Anterior</th>
                  <th style={{ ...celda, textAlign: 'center' }}>Día</th>
                  <th style={{ ...celda, textAlign: 'center' }}>Fecha</th>
                  <th style={{ ...celda, textAlign: 'right' }}>Importe del Día</th>
                  <th style={{ ...celda, textAlign: 'center' }}>Bonificar</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => {
                  const key = claveDia(fila.registro.id, fila.indice);
                  return (
                    <tr key={key}>
                      {fila.esPrimera && (
                        <td style={celda} rowSpan={fila.numDias}>
                          {fila.registro.empleado_numero_y_nombre_completo}
                        </td>
                      )}
                      {fila.esPrimera && (
                        <td style={celda} rowSpan={fila.numDias}>
                          {fila.descuentoAnterior
                            ? `${fila.descuentoAnterior.clave || `#${fila.descuentoAnterior.id}`} - ${fila.descuentoAnterior.descuento_concepto || ''}`
                            : (
                              <Text type="danger">Sin Descuento Empleado asociado</Text>
                            )}
                        </td>
                      )}
                      <CeldasDia
                        fila={fila}
                        diasClave={key}
                        diasExcluidos={diasExcluidos}
                        onToggleDia={(diaKey, checked) => setDiasExcluidos((prev) => ({
                          ...prev,
                          [diaKey]: !checked,
                        }))}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Col>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              name={namePeriodoDeNomina}
              label="Periodo de Nómina"
              rules={rules.required}
              hasFeedback
            >
              <Select
                dataSource={periodosDeNomina}
                render={(value) => `${value?.no_de_inicio_periodo ?? ''} (${value?.fecha_inicial} al ${value?.fecha_final_ejercicio})`}
                onChange={() => form.setFieldValue('periodo_de_nomina_detalle', undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              name="periodo_de_nomina_detalle"
              label="Periodo de Nómina Detalle"
              rules={rules.required}
              hasFeedback
            >
              <Select
                dataSource={periodosDeNominaDetalle}
                render={(value) => `${value?.numero_de_nomina} (${value?.fecha_inicial} al ${value?.fecha_final})`}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              name="observaciones"
              label="Observaciones"
            >
              <Input.TextArea allowClear rows={3} />
            </Form.Item>
          </Col>
        </Form>
      </Modal>
    </Spin>
  );
};

export default IncidenciaBonificar;
